import { inject, Injectable } from '@angular/core';
import { IDefaultProdukt, IProduktAuswahlDetails, IProduktSelektion, PreisIndikation, PriceSheet } from '@mwe/models';
import { TariffSelectionStateService } from './tariff-selection-state.service';
import { isArrayWithMinOneItem, isPostcodeInMundlCity, isProductCategoryInternet } from '@mwe/utils';
import { ProductPriceIndicationCategory } from '@mwe/constants';
import { OrderService } from '../../order/order.service';
import { LoggingService } from '../../logging/logging.service';
import { PriceSheetService } from '../../price-sheet/price-sheet.service';
import { catchError, firstValueFrom, forkJoin, map, Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TariffSelectionLogic {
  private orderService = inject(OrderService);
  private priceSheetService = inject(PriceSheetService);
  private tariffSelectionStateService = inject(TariffSelectionStateService);
  private loggingService = inject(LoggingService);

  mockCategories = ['Internet', 'Iptv', 'Voip'];

  async getPriceIndications(postCode: string, categories: string[]): Promise<PreisIndikation[]> {
    let missingCategories = categories.filter(c => {
      return this.tariffSelectionStateService.indications.every(i => i.sparte.toLowerCase() !== c.toLowerCase());
    });
    const internetCategories = missingCategories.find(mc => isProductCategoryInternet(mc));

    if (internetCategories?.length > 0) {
      missingCategories = missingCategories.filter(mc => !isProductCategoryInternet(mc));
      missingCategories.push(ProductPriceIndicationCategory.SUPERSCHNELL);
    }

    if (isArrayWithMinOneItem(missingCategories)) {
      const indicationRequests = missingCategories.map(category => this.orderService.loadPriceIndications(postCode, category));
      const newIndications = (await Promise.all(indicationRequests)).flat();
      this.tariffSelectionStateService.indications = [...this.tariffSelectionStateService.indications, ...newIndications];
    }

    return this.tariffSelectionStateService.indications;
  }

  async initializePriceSheets(
    allProducts: IProduktAuswahlDetails[],
    defaultProducts: IDefaultProdukt,
    preselectedProducts: IProduktSelektion,
    zipCode: string,
  ): Promise<void> {
    const selectedDefaultProducts: IProduktAuswahlDetails[] = allProducts?.filter(
      produkt => defaultProducts[produkt?.sparte?.toLowerCase()]?.tarifKey === produkt?.tarif?.ISUTarifKey,
    );

    let availablePreselectedProducts: IProduktAuswahlDetails[] = [];
    if (preselectedProducts) {
      availablePreselectedProducts = allProducts?.filter(
        produkt => preselectedProducts[produkt?.sparte?.toLowerCase()]?.tarifKey === produkt?.tarif?.ISUTarifKey,
      );
    }

    // merge defaults and preselected products for price sheet searches
    let productsToInitialize: IProduktAuswahlDetails[] = Array.from(
      new Map(
        [...selectedDefaultProducts, ...availablePreselectedProducts]
          .filter(item => !!item?.sparte)
          .map(item => [item?.sparte?.toLowerCase(), item]),
      ).values(),
    );

    //edge case default products and all available products dont have a single match -> just use the first one
    if (!isArrayWithMinOneItem(productsToInitialize)) {
      productsToInitialize = [allProducts[0]];
    }
    await this.setPriceSheetSearchesFromProducts(zipCode, productsToInitialize);
  }

  async setPriceSheetSearchesFromProducts(postCode: string, produkte: IProduktAuswahlDetails[]): Promise<void> {
    const priceSheetRequests: Observable<PriceSheet>[] = this.getPriceSheetRequests(produkte, postCode);

    if (priceSheetRequests) {
      const newPriceSheets$ = forkJoin(
        priceSheetRequests?.map(req$ =>
          req$.pipe(
            map(priceSheet => [priceSheet] as PriceSheet[]),

            catchError(err => {
              this.loggingService.logError(err, 'PriceSheet request failed');
              return of([] as PriceSheet[]);
            }),
          ),
        ),
      ).pipe(map(arrays => arrays.flat()));

      this.tariffSelectionStateService.priceSheets = this.mergePriceSheets(
        this.tariffSelectionStateService.priceSheets,
        await firstValueFrom(newPriceSheets$),
      );
    }
  }

  private mergePriceSheets(alreadySetPriceSheets: PriceSheet[], responses: PriceSheet[]) {
    return Array.from(
      new Map([...alreadySetPriceSheets, ...responses].filter(item => !!item.isuTariffKey).map(item => [item.isuTariffKey, item])).values(),
    );
  }

  private getPriceSheetRequests(produkte: IProduktAuswahlDetails[], postCode: string): Observable<PriceSheet>[] {
    return produkte?.map(produkt => {
      const isuTariffKey = produkt?.tarif?.ISUTarifKey;
      return this.priceSheetService
        .searchPriceSheet({
          isuTariffKey: isuTariffKey,
          deadlineDate: produkt?.bedingungen?.angebotsdatum,
          isViennaAddress: isPostcodeInMundlCity(postCode),
        })
        .pipe(
          map(
            result =>
              ({
                ...result,
                isuTariffKey: isuTariffKey,
                category: produkt.sparte.toLowerCase(),
              }) as PriceSheet,
          ),
        );
    });
  }

  async getUpdatedPriceIndications(
    plz: string,
    category: string,
    tariffKey?: string,
    options?: string,
    voucherCode?: string,
  ): Promise<{
    indication: PreisIndikation;
    allIndications: PreisIndikation[];
  }> {
    const response = await this.orderService.loadPriceIndications(plz, category, tariffKey, options, voucherCode);
    return {
      indication: response?.[0],
      allIndications: response,
    };
  }

  // after tariff-options-change we need new priceIndications for this category to show updated prices
  async updatePriceIndications(plz: string, category: string, tariffKey?: string, options?: string): Promise<void> {
    if (this.mockCategories.includes(category)) {
      return Promise.resolve();
    }

    const updatedTariffIndications = await this.orderService.loadPriceIndications(plz, category, tariffKey, options);
    // remove old indications
    const indications = this.tariffSelectionStateService.indications.filter(i => {
      return updatedTariffIndications.every(ui => ui.tarif.ISUTarifKey !== i.tarif.ISUTarifKey);
    });
    // add new indications
    this.tariffSelectionStateService.indications = indications.concat(updatedTariffIndications);
  }

  wasTariffOptionsFormPopupOpenedFor(category: string): boolean {
    return this.tariffSelectionStateService.tariffOptionsFormPopupAlreadyOpenedFor.some(c => c === category);
  }

  markCategoryAsOpened(category: string): void {
    if (this.wasTariffOptionsFormPopupOpenedFor(category)) {
      return;
    }

    const categories = this.tariffSelectionStateService.tariffOptionsFormPopupAlreadyOpenedFor.concat(category);
    this.tariffSelectionStateService.tariffOptionsFormPopupAlreadyOpenedFor = categories;
  }

  hasMatchingPriceSheet(isuTariffKey: string): boolean {
    return this.priceSheetService.hasMatchingPriceSheet(isuTariffKey);
  }
}
